import type { EventInput, Meta } from '@numistic/contracts'
import { useCounterparties } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Field, Input, Select } from '@numistic/ui'
import { useId } from 'react'

import { selectable } from '../meta/widgets/ref-options.js'

export type EventDraft = Omit<EventInput, 'kind'>

const TOP = ['RUB', 'USD', 'EUR']

/** Поля события (покупка, продажа…): дата, сумма в валюте, контрагент, источник, лот. */
export function EventFields({
  meta,
  value,
  onChange,
  withSource = true,
}: {
  meta: Meta
  value: EventDraft
  onChange: (value: EventDraft) => void
  withSource?: boolean
}) {
  const { t } = useTranslation()
  const set = (patch: Partial<EventDraft>) => onChange({ ...value, ...patch })
  const counterparties = useCounterparties().data ?? []
  const listId = useId()
  const typed = value.counterparty?.trim().toLocaleLowerCase('ru') ?? ''
  const known = counterparties.find((c) => c.name.toLocaleLowerCase('ru') === typed)
  const kindName = (code: string | null) =>
    meta.enums.acquisition_source?.find((o) => o.code === code)?.name.ru
  const currencies = [...TOP, ...meta.currencies.map((c) => c.code).filter((c) => !TOP.includes(c))]
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Field label={t('specimen.date')}>
        <Input
          type="date"
          value={value.occurredOn ?? ''}
          onChange={(e) => set({ occurredOn: e.target.value })}
        />
      </Field>
      <Field label={t('specimen.amount')}>
        <Input
          inputMode="decimal"
          value={value.amount ?? ''}
          onChange={(e) => set({ amount: e.target.value.replace(',', '.') || null })}
        />
      </Field>
      <Field label={t('specimen.currency')}>
        <Select value={value.currency ?? 'RUB'} onChange={(e) => set({ currency: e.target.value })}>
          {currencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t('specimen.counterparty')}
        className="md:col-span-2"
        hint={
          typed && !known
            ? t('counterparties.willAdd')
            : known?.kind
              ? kindName(known.kind)
              : undefined
        }
      >
        <Input
          list={listId}
          autoComplete="off"
          placeholder={t('counterparties.placeholder')}
          value={value.counterparty ?? ''}
          onChange={(e) => {
            const text = e.target.value
            const match = counterparties.find(
              (c) => c.name.toLocaleLowerCase('ru') === text.trim().toLocaleLowerCase('ru'),
            )
            // Выбрали знакомого продавца — источник («магазин», «аукцион») подставляется сам.
            set({
              counterparty: text || null,
              ...(withSource && match?.kind && !value.sourceCode ? { sourceCode: match.kind } : {}),
            })
          }}
        />
      </Field>
      <datalist id={listId}>
        {counterparties.map((c) => (
          <option key={c.id} value={c.name}>
            {kindName(c.kind) ?? ''}
          </option>
        ))}
      </datalist>
      {withSource ? (
        <Field label={t('specimen.source')}>
          <Select
            value={value.sourceCode ?? ''}
            onChange={(e) => set({ sourceCode: e.target.value || null })}
          >
            <option value="">{t('common.notSet')}</option>
            {selectable(
              meta.enums.acquisition_source ?? [],
              (o) => o.code === value.sourceCode,
            ).map((o) => (
              <option key={o.code} value={o.code}>
                {o.name.ru}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field label={t('specimen.lot')}>
        <Input
          value={value.lotNumber ?? ''}
          onChange={(e) => set({ lotNumber: e.target.value || null })}
        />
      </Field>
      <Field label={t('specimen.fees')}>
        <Input
          inputMode="decimal"
          value={value.fees ?? ''}
          onChange={(e) => set({ fees: e.target.value.replace(',', '.') || null })}
        />
      </Field>
      <Field label={t('specimen.place')}>
        <Input value={value.place ?? ''} onChange={(e) => set({ place: e.target.value || null })} />
      </Field>
    </div>
  )
}

/** Черновик → событие: пустые строки убираются, без даты и суммы события нет. */
export function cleanEvent(draft: EventDraft): EventDraft | undefined {
  if (!draft.occurredOn) return undefined
  const out: EventDraft = { occurredOn: draft.occurredOn }
  for (const [k, v] of Object.entries(draft))
    if (v !== null && v !== '' && v !== undefined) (out as Record<string, unknown>)[k] = v
  if (out.amount && !out.currency) out.currency = 'RUB'
  return out
}
